import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import AuthContext from "../../context/AuthProvider";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useLoginRedirect } from "./useHomeWidgets";

const dedupeAndSort = (posts) =>
  posts
    .filter((post, index, self) => index === self.findIndex((t) => t.uuid === post.uuid))
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .sort((a, b) => b.pinned - a.pinned);

// The social feed of the home page (same requests as Ekloges.jsx): the
// newest page on mount and whenever the unseen-notification count changes,
// older pages through `sentryRef` (infinite scroll), pinned posts first.
export default function usePostsFeed() {
  const axiosPrivate = useAxiosPrivate();
  const handleError = useLoginRedirect();
  const { notifLength } = useContext(AuthContext);
  const [rawPosts, setRawPosts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const lastIdRef = useRef(null);
  const loadingMoreRef = useRef(false);

  const append = useCallback((data) => {
    if (!Array.isArray(data) || !data.length) return;
    setRawPosts((prev) => [...prev, ...data]);
  }, []);

  // Newest page: on mount and when notifications arrive.
  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const response = await axiosPrivate.get("/posts/all", { signal: controller.signal });
        const data = response.data;
        append(data);
        if (Array.isArray(data) && data.length) {
          if (lastIdRef.current === null) lastIdRef.current = data[data.length - 1].id;
          setTotalCount(data[0].count ?? 0);
        }
      } catch (err) {
        handleError(err);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [axiosPrivate, notifLength, append, handleError]);

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current || lastIdRef.current === null) return;
    loadingMoreRef.current = true;
    try {
      const response = await axiosPrivate.get("/posts/all", {
        params: { page: lastIdRef.current },
      });
      const data = response.data;
      append(data);
      if (Array.isArray(data) && data.length) lastIdRef.current = data[data.length - 1].id;
    } catch (err) {
      handleError(err);
    } finally {
      loadingMoreRef.current = false;
    }
  }, [axiosPrivate, append, handleError]);

  // Called by Post (as getPosts) and after posting: reload the newest page.
  const reloadNewest = useCallback(async () => {
    try {
      const response = await axiosPrivate.get("/posts/all", { params: { page: undefined } });
      if (Array.isArray(response.data)) {
        // Newer copies of the same posts win.
        setRawPosts((prev) => [...response.data, ...prev]);
      }
    } catch (err) {
      handleError(err);
    }
  }, [axiosPrivate, handleError]);

  const deletePost = useCallback((uuid) => {
    setRawPosts((prev) => prev.filter((post) => post.uuid !== uuid));
  }, []);

  const posts = useMemo(() => dedupeAndSort(rawPosts), [rawPosts]);
  const hasMore = posts.length < totalCount;

  // Callback ref for the element at the end of the feed.
  const observerRef = useRef(null);
  const sentryRef = useCallback(
    (node) => {
      observerRef.current?.disconnect();
      if (!node) return;
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore();
      });
      observerRef.current.observe(node);
    },
    [loadMore]
  );
  useEffect(() => () => observerRef.current?.disconnect(), []);

  return { posts, loading, hasMore, sentryRef, reloadNewest, deletePost };
}
